"use client";

import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Auditfaq() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "What is included in a hotel revenue audit?",
    answer: "Our comprehensive audit evaluates your current OTA performance, rate parity status, tech stack efficiency, and competitor benchmarking. We identify revenue leakage points and provide actionable strategies to optimize your digital presence and increase direct bookings."
  }, {
    question: "How long does the revenue audit process take?",
    answer: "A standard revenue audit typically takes 5 to 7 business days. This allows us to deep-dive into your historical data, analyze your current market positioning, and prepare a detailed report with prioritized recommendations for your property."
  }, {
    question: "Is the audit suitable for all types of hotels?",
    answer: "Yes. Whether you manage a boutique hotel, a large resort, or an urban business property, our audit is tailored to your specific market segment. We focus on the unique challenges and opportunities relevant to your property type and location."
  }, {
    question: "Do you provide implementation support after the audit?",
    answer: "Absolutely. While the audit provides the roadmap, we offer ongoing revenue management consulting and tech implementation services to help you execute the strategies and achieve sustainable revenue growth."
  }, {
    question: "How does your audit help with OTA performance?",
    answer: "We analyze your OTA listings for content quality, conversion rates, and visibility. Our audit identifies opportunities to improve your ranking, optimize your pricing strategy, and ensure your property stands out to potential guests."
  }, {
    question: "What data do you need from my hotel?",
    answer: "To provide an accurate audit, we typically require access to your PMS reports, channel manager data, and current OTA extranet performance metrics. All data shared is handled with strict confidentiality."
  }, {
    question: "Can this audit help increase direct bookings?",
    answer: "Yes, a core focus of our audit is identifying barriers to direct bookings. We analyze your website's conversion funnel and digital marketing strategy to recommend improvements that shift bookings from high-commission OTAs to your direct channels."
  }, {
    question: "How do I schedule a revenue audit?",
    answer: "You can request an audit through our contact page or by booking a consultation directly. We will schedule a brief discovery call to understand your property's needs and outline the next steps for your revenue transformation."
  }];
  return <section data-section-id="381" id="auditfaq" className="bg-background text-foreground py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16 md:mb-24 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <p className="font-default text-xs tracking-[0.2em] text-slate-500 uppercase mb-6">
              Expert Insights
            </p>
            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-slate-900 tracking-tight">
              Frequently
              <br />
              asked{" "}
              <em className="italic text-blue-700 font-normal">questions</em>
            </h2>
          </div>

          <div className="lg:col-span-5 lg:pt-8 flex flex-col justify-end">
            <p className="font-default text-base md:text-lg text-slate-600 leading-relaxed max-w-md mb-6">
              Common inquiries about our revenue management audits. Need more clarity? Contact us today.
            </p>
            <Link to="/ContactPage" className="inline-flex items-center gap-2 font-default text-xs tracking-[0.2em] uppercase text-blue-900 hover:text-blue-700 transition-colors duration-300 w-fit group">
              Get in touch
              <ArrowRight className="size-3 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div ref={listRef} className={`max-w-[800px] mx-auto transition-all duration-700 ease-out ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-t border-slate-200 last:border-b border-b-0 data-[state=open]:border-blue-200 group">
                <AccordionTrigger className="py-6 md:py-7 hover:no-underline [&>svg]:hidden text-left group/trigger">
                  <span className="font-default text-lg md:text-xl leading-snug text-slate-900 transition-colors duration-300 group-data-[state=open]:italic group-data-[state=open]:text-blue-700 pr-6">
                    {faq.question}
                  </span>
                  <span className="relative flex items-center justify-center size-6 shrink-0 mt-1">
                    <span className="absolute h-px w-4 bg-slate-900 transition-colors duration-300 group-data-[state=open]:bg-blue-700" />
                    <span className="absolute h-4 w-px bg-slate-900 transition-all duration-300 group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0 group-data-[state=open]:bg-blue-700" />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-7 pt-0 pr-12">
                  <p className="font-default text-base leading-[1.7] text-slate-600">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}